//
// Copyright (c) 2011-2020 by Curalate, Inc.
//

import { CONTENT_LIBRARY_SOURCES_GROUPS } from '@search-filters/constants/contentLibrarySourcesGroups';

(function() {
  const curalate = angular.module('curalate');

  curalate.constant('CONTENT_LIBRARY_CONTENT_GROUPS', {
    ALL_MEDIA: 'AllMedia',
    ALL: 'All',
    TEAM: 'Team',
    COLLECTION: 'Collection',
    ACCOUNT_MENTIONS: 'Account Mentions',
    TRACKED_HASHTAGS: 'trackedHashtags',
    TRACKED_USERS: 'trackedUsers',
    TRACKED_BUSINESS_USERS: 'trackedBusinessUsers',
    NOTHING: 'Nothing',
  });

  curalate.constant('CONTENT_LIBRARY_SOURCES_GROUPS', CONTENT_LIBRARY_SOURCES_GROUPS);

  curalate.service('ContentLibraryService', [
    '$http',
    '$log',
    '$rootScope',
    '$timeout',
    'COLLECTION_SETTINGS',
    'CONTENT',
    'CONTENT_LIBRARY_CONTENT_GROUPS',
    'CONTENT_LIBRARY_SOURCES_GROUPS',
    'ContentLibraryApiService',
    'ErrorNotificationService',
    'ProcessingVideosService',
    'rx',
    'ToastNotificationService',
    'TOASTS',
    'TrackingService',
    'UniversalImageActionsHelpers',
    'UniversalImageActionsItemType',
    'UniversalItemId',
    function(
            $http,
            $log,
            $rootScope,
            $timeout,
            COLLECTION_SETTINGS,
            CONTENT,
            CONTENT_LIBRARY_CONTENT_GROUPS,
            CONTENT_LIBRARY_SOURCES_GROUPS,
            ContentLibraryApiService,
            ErrorNotificationService,
            ProcessingVideosService,
            rx,
            ToastNotificationService,
            TOASTS,
            TrackingService,
            UniversalImageActionsHelpers,
            UniversalImageActionsItemType,
            UniversalItemId,
        ) {
      const self = this;

      let itemLimit = null;
      let itemIncrement = 30;
      let pagesToLoad = 10;

      let endOfItemsReached = false;

      const EXTRA_ADDED_ITEMS_LIMIT = 50;

      let allItems = [];
      let isInitialPageLoad = true;
      let shouldPreloadThumbnailUrl = false;
      let outstandingAsyncAction = null;
      let queuedUploads = [];
      let userQueryParams = {};

            /**
             * RxJS subjects.
             */
      const contentApiSubject = new rx.Subject();
            // Receives content directly from the Content REST API. The service will use these
            // results as a cache for sending items to subscribers on itemStream (below).
      const contentApiStream = contentApiSubject.mergeAll();
            // Streams increments of items when requested from a subscriber.
      this.itemStream = new rx.Subject();
            // Streams active items in upload/processing state to subscribers.
      this.queuedUploadsStream = new rx.Subject();
            // Streams single newly uploaded content items to subscribers.
      this.newContentUploadedStream = new rx.Subject();
            // Streams single video content items to subscribers when they complete processing.
      this.videoCompleteStream = new rx.Subject();
            // Streams single content items to subscribers when they are removed.
      this.removeItemStream = new rx.Subject();
            // Streams status updates to the content service's async state.
      this.asyncStatusStream = new rx.Subject();
            // Streams whether the library is fully loaded.
      this.endOfItemsReachedStream = new rx.BehaviorSubject(false);
      this.itemCountObservable = new rx.ReplaySubject(1);
      this.itemStatsObservable = new rx.ReplaySubject(1);
      this.hasLibraryContentObservable = new rx.ReplaySubject(1);

            // private stream used to cancel in-flight api requests
      const stopSource = new rx.Subject();
      const stopStream = stopSource.publish();
      stopStream.connect();

            /**
             * Private Content Service functions
             */

      const updateHasLibraryContent = () => {
        ContentLibraryApiService.getContentItemsAsync({ limit: 1 }).then((r) => {
          self.hasLibraryContentObservable.next(r.contentItems.length > 0);
        });
      };

      const abortPreviousCalls = function() {
        stopSource.next(rx.Observable.of({}));
      };

      const removeQueuedUpload = function(itemUuid) {
        queuedUploads = _(queuedUploads).reject((uuid) => { return itemUuid === uuid; });
        self.queuedUploadsStream.next(queuedUploads);
      };

      this.removeItem = function(itemId) {
        allItems = _(allItems).reject((item) => { return item.id === itemId; });
        self.removeItemStream.next(itemId);
      };

      const deleteContentItem = function(item) {
        const partialUniversalItemId = new UniversalItemId(item.itemType, item.id).toString();
        item.disabled = true;
        return ContentLibraryApiService.removeContentItemAsync(partialUniversalItemId).then((response) => {
          if (response.success) {
            self.removeItem(item.id);
          }
        }).catch((error) => {
          $log.error(error);
          ErrorNotificationService.showDefaultErrorDialog(error);
        }).finally(() => {
          item.disabled = false;
          updateHasLibraryContent();
        });
      };

      const updateItemStream = function(items) {
        self.itemStream.next(items);
      };

      const errorItemStream = function(err) {
        self.itemStream.error(err);
      };

      const completeItemStream = function() {
        self.itemStream.complete();
      };

      const updateAsyncStatus = function(newAsyncStatus) {
        outstandingAsyncAction = newAsyncStatus;
        self.asyncStatusStream.next(outstandingAsyncAction);
      };

            /**
             * Useful for testing to complete the underlying stream which publishes api responses.
             */
      this.completeContentApiStream = function() {
        contentApiSubject.complete();
      };

            // Parameters for requesting AddedToLibrary items to merge into other items
      const addedToLibraryItemsExtraParams = {
        contentGroup: 'AddedToLibrary',
        orderBy: 'AddedToLibraryTimestamp',
        offset: 0,
        limit: EXTRA_ADDED_ITEMS_LIMIT,
      };

            // Parameters for requesting AddedToCollection items to merge into other items
      const addedToCollectionItemsExtraParams = function(collectionId) {
        return {
          contentGroup: 'AddedToCollection',
          collectionId,
          orderBy: 'AddedToCollectionTimestamp',
          offset: 0,
          limit: EXTRA_ADDED_ITEMS_LIMIT,
        };
      };

            /**
             * @param {Object} userParams - Filters supplied by user actions for filtering items
             * @param {Object} extraParams - Filter options to merge into userParams
             * @returns {Observable} Observable which resolves with retrieved content library items.
             */
      const requestItemsWithParams = function(userParams, extraParams) {
        const allParams = _(angular.copy(userParams)).extend(extraParams);
        const promise = ContentLibraryApiService.getContentItemsAsync(allParams);
        return rx.Observable.fromPromise(promise)
                    .takeUntil(stopStream)
                    .map((response) => {
                      return response.contentItems;
                    });
      };

            /**
             * Returns the items most recently added to your library and collection, given the specified filters.
             *
             * _NOTE_ Some of the filters are overriden to return the information required (e.g. `filterParams.orderBy` is overwritten)
             *
             * @param {Object} filterParams Filters supplied by user acctions for filtering items. See ContentLibraryApiService.getContentItemsAsync()
             *      for more information on the structure of the data itself.
             *
             * @returns {[Observable, Observable]} A tuple of rx obvservables
             *      tuple[0]: An rx observable that resolves with the items most recently added to the library
             *      tuple[1]: An rx observable that resolves with the items most recently added to the collection specified in `userQueryParams`
             */
      function loadRecentlyAddedContent(filterParams) {
                // we want to interleave the first N items that were added to library into the rest of the items
        const filtersObs = rx.Observable.of(userQueryParams);
        const addedToLibraryObservable = filtersObs
                    .flatMap((filters) => {
                      if (
                            _(filters.curalateUserIds).size() > 0 ||
                            _(filters.sourcesGroups).contains(CONTENT_LIBRARY_SOURCES_GROUPS.TEAM_MEDIA) ||
                            _(filters.sourcesGroups).contains(CONTENT_LIBRARY_SOURCES_GROUPS.ALL_MEDIA) ||
                            _(filters.userIds).size() > 0 ||  // old user dropdown
                            _(filters.contentGroup).contains(CONTENT_LIBRARY_CONTENT_GROUPS.TEAM) || // old user dropdown
                            _(filters.contentGroup).contains(CONTENT_LIBRARY_CONTENT_GROUPS.ALL) || // old user dropdown
                            _(filters.contentGroup).size() === 0 // defaults to ALL which includes library content
                        ) {
                        return requestItemsWithParams(filterParams, addedToLibraryItemsExtraParams);
                      }
                      return rx.Observable.of([]);
                    });

        const collectionIdObs = rx.Observable.of(userQueryParams.collectionId);
        const addedToCollectionObservable = collectionIdObs.flatMap((collectionId) => {
          if (!collectionId) {
            return rx.Observable.of([]);
          }
          return requestItemsWithParams(filterParams, addedToCollectionItemsExtraParams(collectionId));
        });

        return [addedToLibraryObservable, addedToCollectionObservable];
      }

            /**
             * Fetches Content Items from API.
             * @param refresh: boolean, specifies if we're refreshing the list of content.
             */
      function loadMoreContent(refresh) {
        const LIMIT = pagesToLoad + itemIncrement;
        let offset;
        if (!refresh && (outstandingAsyncAction !== null || queuedUploads.length !== 0 || endOfItemsReached)) {
          return;
        }
                // If we're refreshing the list (e.g. when a user changes filters), set offset to zero.
        if (refresh) {
          updateAsyncStatus(CONTENT.ASYNC_ACTIONS.REFRESH);
          endOfItemsReached = false;
          self.endOfItemsReachedStream.next(endOfItemsReached);
          offset = 0;
          isInitialPageLoad = true;
        } else {
          offset = allItems.length;
          updateAsyncStatus(CONTENT.ASYNC_ACTIONS.LOADING_CONTENT);
        }

                // As we rollout, it's going to be useful to know how many items users are loading.
        TrackingService.recordCount(TrackingService.CATEGORY_UNIVERSAL_UPLOADER, 'ContentLibraryLoad', 'ItemsLoaded', offset);

        const systemParams = {
          offset,
          limit: LIMIT,
          hydrateWithDeetsItems: true,
        };
        const requestParams = _(systemParams).extend(userQueryParams);

        if (refresh) {
          abortPreviousCalls();
        }

        let contentObservable = rx.Observable.fromPromise(ContentLibraryApiService.getContentItemsAsync(requestParams))
                    .takeUntil(stopStream)
                    .do((response) => {
                      endOfItemsReached = response.numAvailable === 0;
                      self.endOfItemsReachedStream.next(endOfItemsReached);
                    }).map((response) => {
                      self.itemCountObservable.next(response.totalCount);
                      self.itemStatsObservable.next(response.contentStats);
                      return {
                        items: response.contentItems,
                      };
                    });

        const isSortingByDate = !requestParams.orderBy ||
                    requestParams.orderBy[0] === COLLECTION_SETTINGS.SORT_KEYS.TIME;

                // If we are sorting by date, then include items recently added to the library/collection. For items
                // recently added, sort by their addedToLibraryTimestamp/addedToCollectionTimestamp rather than by createdTime
        // if (refresh && isSortingByDate) {
        //   const [addedToLibraryObservable, addedToCollectionObservable] = loadRecentlyAddedContent(requestParams);

        //   contentObservable = rx.Observable.zip(
        //                 contentObservable,
        //                 addedToLibraryObservable,
        //                 addedToCollectionObservable,
        //                 (contentItems, addedToLibraryItems, addedToCollectionItems) => ({
        //                   items: contentItems.items,
        //                   addedToLibraryItems,
        //                   addedToCollectionItems,
        //                 }),
        //             );
        // }

                // If we're refreshing the list, clear out all existing items.
        if (refresh) {
          allItems = [];
        }

        contentApiSubject.next(contentObservable.catch((e) => {
                    // catch any error from the api call and return an empty array instead of propagating error to contentApiSubject
          $log.error(`Error retrieving content items, ${e}`);
          return rx.Observable.of({
            items: [],
          });
        }));
      }

            /**
             * "Preloads" an item by asynchronously loading the resource
             * and marking it as loaded (so we can fade in) once complete.
             * @param item to preload
             */
      const preloadContentItem = function(item) {
        const image = new Image();
        image.contentId = item.id;
        image.onload = function() {
          $rootScope.$applyAsync(() => {
            item.imageLoaded = true;
          });
        };
        if (shouldPreloadThumbnailUrl) {
          image.src = item.thumbnailUrl;
        } else {
          image.src = item.imageUrl;
        }
      };

            /**
             * Preloads an array of Content Items.
             * @param items to preload.
             */
      const preloadContent = function(items) {
        _(items).each((item) => {
          preloadContentItem(item);
        });
      };

      const attachHandlers = function(item) {
        item.deleteItemCallback = function() {
          return deleteContentItem(item);
        };

        if (item.videoId && item.videoStatus !== CONTENT.VIDEO_STATUS.READY) {
          ProcessingVideosService.startPollingForUpload(item.id);
        }
      };

            /**
             * Returns the timestamp to sort the item by. The possible timestamps to sort by
             * are `itemToSort.createdTime`, `itemToSort.addedToLibraryTimestamp` or
             * `itemToSort.addedToCollectionTimestamp`
             *
             * @param {Object} item The item to get the sort by timestamp from
             */
      function getSortTimestamp(item) {
        return Math.max(item.createdTime,
                    item.addedToLibraryTimestamp || 0,
                    item.addedToCollectionTimestamp || 0);
      }

            /**
             * Merges the items returned by the various "most-recent" queries into a single sorted array. The merge
             * is also kept in sorted order by utilizing the appropriate timestamps: createdTime, addedToLibraryTimestamp,
             * and addedToCollectionTimestamp.
             *
             * @param {Object} items An array of items pre-sorted by the server
             * @param {Object} addedToLibraryItems Optional, iff `addedToCollectionItems` is also undefined. An array of
             *      items most recently added to the library.
             * @param {Object} addedToCollectionItems Optional, iff `addedToLibraryItems` is also undefined. An array of
             *      items most recently added to the collection.
             */
      function mergeMostRecentItems(items, addedToLibraryItems, addedToCollectionItems) {
        function getMostRecentItems(itemsToFilterAndSort) {
          const mostRecentItemMap = _(itemsToFilterAndSort).reduce((itemMap, item) => {
            const currentlyMostRecentItem = itemMap.get(item.id);

            let mostRecentItem;
            if (!currentlyMostRecentItem) {
              mostRecentItem = item;
            } else {
              mostRecentItem = getSortTimestamp(currentlyMostRecentItem) > getSortTimestamp(item) ? currentlyMostRecentItem : item;
            }

            itemMap.set(mostRecentItem.id, mostRecentItem);

            return itemMap;
          }, new Map());

          return Array.from(mostRecentItemMap.values());
        }

        const itemsToFilterAndSort = [...allItems, ...items, ...addedToLibraryItems, ...addedToCollectionItems];
        const mostRecentItems = getMostRecentItems(itemsToFilterAndSort);

                // Sort by date desc
        return mostRecentItems.sort((item1, item2) => getSortTimestamp(item2) - getSortTimestamp(item1));
      }

      contentApiStream.subscribe(
                ({
                    items,
                    addedToLibraryItems,
                    addedToCollectionItems,
                }) => {
                  items.forEach(item => attachHandlers(item));

                    // addedToLibraryItems and addedToCollectionItems only exist when we are sorting
                    // by most recent. In all other instances, just pass through the existing sort
                  if (addedToLibraryItems && addedToCollectionItems) {
                    addedToLibraryItems.forEach(item => attachHandlers(item));
                    addedToCollectionItems.forEach(item => attachHandlers(item));

                    allItems = mergeMostRecentItems(items, addedToLibraryItems, addedToCollectionItems);
                  } else {
                    allItems = [...allItems, ...items];
                  }

                  const itemsToStream = _(allItems).first(itemLimit);
                  preloadContent(itemsToStream);
                  isInitialPageLoad = false;
                  updateItemStream(itemsToStream);
                  updateAsyncStatus(null);
                },
                (e) => {
                  $log.error(e);
                  errorItemStream(e);
                },
                () => {
                  completeItemStream();
                },
            );

            /**
             * Public service functions
             */

      this.setItemIncrement = function(increment) {
        itemIncrement = increment;
      };
      this.setItemLimit = function(newItemLimit) {
        itemLimit = newItemLimit;
      };

      this.setPagesToLoad = function(newPagesToLoad) {
        pagesToLoad = newPagesToLoad;
      };

      this.setFilterParams = function(paramsObject) {
        userQueryParams = paramsObject;
      };

      this.setPreloadThumbnailUrl = function(shouldPreload) {
        shouldPreloadThumbnailUrl = shouldPreload;
      };

      this.initialize = function() {
        loadMoreContent(true);
        updateHasLibraryContent();
      };

            /**
             * Call this function to retrieve additional content items.
             * @param currentItemCount
             */
      this.fetchAdditionalContent = function(currentItemCount) {
        if (outstandingAsyncAction !== null || queuedUploads.length !== 0) {
          return;
        }

        if (!isInitialPageLoad) {
          itemLimit = currentItemCount + itemIncrement;
        }

        const lookaheadLength = itemLimit + (3 * itemIncrement);
        if (lookaheadLength > allItems.length) {
                    // Anticipate loading new content when we're approaching the end of the available loaded items.
          loadMoreContent(false);
        }

        if (currentItemCount < allItems.length) {
          const itemsToStream = _.chain(allItems)
                        .first(itemLimit)
                        .rest(currentItemCount)
                        .value();
          preloadContent(itemsToStream);

                    // Send all new items to stream.
          updateItemStream(itemsToStream);
        }
      };

      this.getParams = function(filters) {
        return _(userQueryParams).extend(filters);
      };

            /**
             * Listen for events from Content-related services.
             */
      $rootScope.$on('universal-upload:content-failed', (event, item) => {
        removeQueuedUpload(item.uuid);
      });

      const initializeAndStreamNewUpload = function(item) {
        attachHandlers(item);
        allItems.unshift(item);
        preloadContentItem(item);

        self.newContentUploadedStream.next(item);
      };

      $rootScope.$on('universal-upload:content-uploaded', (event, newItem) => {
        const params = _(userQueryParams).extend({
          itemId: new UniversalItemId(UniversalImageActionsItemType.mediaItem, newItem.id).toString(),
        });
        ContentLibraryApiService.getContentItemsAsync(params).then((response) => {
          const items = response.contentItems;
          const item = items[0];
          if (item) {
            initializeAndStreamNewUpload(item);
          } else {
            ToastNotificationService.success({
              title: 'Content hidden!',
              message: 'Your newly uploaded content is hidden by your current filters.',
              options: _.extend({}, TOASTS.DEFAULT_TOAST_OPTIONS, {
                hideProgress: true,
                customIconClass: 'fa fa-check-circle-o fa-lg',
              }),
            });
          }
          updateHasLibraryContent();
          removeQueuedUpload(newItem.uuid);
        });
      });

      $rootScope.$on('universal-upload:add-queued-upload', (event, uuid) => {
        queuedUploads = [uuid, ...queuedUploads];
        self.queuedUploadsStream.next(queuedUploads);
      });

      const removeItems = function(params) {
        const removedItems = _(params.items).map((partialItemId) => {
          return UniversalImageActionsHelpers.parsePartialUniversalItemId(partialItemId).itemId;
        });

        _(removedItems).each((item) => {
          self.removeItem(item);
        });

        $timeout(() => {
          updateHasLibraryContent();
          $rootScope.$broadcast('iso-layout');
        });
      };

      $rootScope.$on('uia:remove-items-from-collection', (event, params) => {
        removeItems(params);
      });

      $rootScope.$on('uia:remove-items-from-library', (event, params) => {
        removeItems(params);
      });

      $rootScope.$on('scheduler:videos-finished-processing', (event, itemIds) => {
        const params = {
          itemIds: _(itemIds).map((itemId) => { return new UniversalItemId(UniversalImageActionsItemType.mediaItem, itemId).toString(); }),
        };
        ContentLibraryApiService.getContentItemsAsync(params).then((response) => {
          const items = response.contentItems;
          _(items).each((item) => {
            allItems = _(allItems).reject((modelItem) => {
              return item.id === modelItem.id;
            });
            item.deleteItemCallback = function() {
              return deleteContentItem(item);
            };
            const insertIndex = _(allItems).findIndex((i) => {
              return item.createdTime > i.createdTime;
            });
            allItems.splice(insertIndex, 0, item);
            preloadContentItem(item);

            self.videoCompleteStream.next(item);
          });
        });
      });
    },
  ]);
})();